import { useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Field, Input, Textarea } from '@/components/ui/form';
import { Modal } from '@/components/ui/system';
import { errorMessage } from '@/lib/utils';
import { useDraftGuard } from '@/lib/use-draft-guard';
import { ContactLabelEditor } from '@/components/domain/contact-editors';

type Edit = {
  key: string;
  label: string;
  context?: string;
} & (
  | {
      kind: 'text';
      initial: string;
      value: string;
      multiline: boolean;
      onSave: (value: string) => Promise<unknown>;
    }
  | {
      kind: 'labels';
      initial: string[];
      value: string[];
      onSave: (value: string[]) => Promise<unknown>;
    }
);

/** One field at a time; save only that field and retain failed drafts. */
export function useTableFieldEditor(onSaved: () => Promise<unknown>) {
  const [edit, setEdit] = useState<Edit | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const saving = useRef(false);
  const dirty =
    !!edit &&
    (edit.kind === 'labels'
      ? JSON.stringify(edit.value) !== JSON.stringify(edit.initial)
      : edit.value !== edit.initial);
  const guard = useDraftGuard(dirty, busy);
  const cancel = () => {
    if (!saving.current) {
      setEdit(null);
      setError('');
    }
  };
  const guardAction = (action: () => void) =>
    guard(() => {
      cancel();
      action();
    });

  const begin = (
    key: string,
    label: string,
    value: string,
    onSave: (value: string) => Promise<unknown>,
    multiline = false,
    context?: string,
  ) => {
    if (edit?.key === key) return;
    guardAction(() =>
      setEdit({ kind: 'text', key, label, initial: value, value, onSave, multiline, context }),
    );
  };
  const startLabels = (
    key: string,
    labels: string[],
    onSave: (value: string[]) => Promise<unknown>,
    context?: string,
  ) => {
    if (edit?.key === key) return;
    guardAction(() =>
      setEdit({
        kind: 'labels',
        key,
        label: 'Labels',
        initial: [...labels],
        value: [...labels],
        onSave,
        context,
      }),
    );
  };

  const save = async () => {
    if (!edit || saving.current) return;
    if (!dirty) {
      cancel();
      return;
    }
    saving.current = true;
    setBusy(true);
    setError('');
    try {
      if (edit.kind === 'labels') await edit.onSave(edit.value);
      else await edit.onSave(edit.value);
      await onSaved();
      setEdit(null);
      toast.success(`${edit.label} saved`);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      saving.current = false;
      setBusy(false);
    }
  };

  const modal = edit ? (
    <FieldEditModal
      key={edit.key}
      edit={edit}
      busy={busy}
      error={error}
      onChange={(value) => {
        setEdit((previous) => {
          if (previous?.kind === 'labels' && Array.isArray(value)) return { ...previous, value };
          if (previous?.kind === 'text' && typeof value === 'string') return { ...previous, value };
          return previous;
        });
        setError('');
      }}
      onSave={save}
      onCancel={cancel}
    />
  ) : null;
  return { start: begin, startLabels, modal, guardAction };
}

function FieldEditModal({
  edit,
  busy,
  error,
  onChange,
  onSave,
  onCancel,
}: {
  edit: Edit;
  busy: boolean;
  error: string;
  onChange: (value: string | string[]) => void;
  onSave: () => Promise<void>;
  onCancel: () => void;
}) {
  const formId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  function submit(event: FormEvent) {
    event.preventDefault();
    event.stopPropagation();
    void onSave();
  }
  function keyDown(event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) {
    if (event.nativeEvent.isComposing) return;
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void onSave();
    }
  }
  const props = {
    'aria-label': `Edit ${edit.label.toLowerCase()}`,
    'aria-invalid': !!error,
    value: edit.kind === 'text' ? edit.value : '',
    disabled: busy,
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange(event.target.value),
    onFocus: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const end = event.currentTarget.value.length;
      event.currentTarget.setSelectionRange(end, end);
    },
    onKeyDown: keyDown,
  };
  return (
    <Modal
      open
      onClose={onCancel}
      initialFocusRef={edit.kind === 'text' ? (edit.multiline ? textareaRef : inputRef) : undefined}
      title={`Edit ${edit.label.toLowerCase()}`}
      description={edit.context}
      footer={
        <>
          <Button
            variant="secondary"
            disabled={busy}
            onClick={onCancel}
            disabledReason="Please wait while the current action finishes."
          >
            Cancel
          </Button>
          {edit.kind === 'labels' ? (
            <Button loading={busy} onClick={() => void onSave()}>
              Save
            </Button>
          ) : (
            <Button type="submit" form={formId} loading={busy}>
              Save
            </Button>
          )}
        </>
      }
    >
      {edit.kind === 'labels' ? (
        <div className="form-stack">
          <ContactLabelEditor
            labels={edit.value}
            disabled={busy}
            onSave={async (labels) => onChange(labels)}
          />
          <p className="text-xs text-fg-subtle">Add or remove labels, then choose Save.</p>
          {error && (
            <p className="text-xs text-danger" role="alert">
              {error}
            </p>
          )}
        </div>
      ) : (
        <form id={formId} onSubmit={submit}>
          <Field
            label={edit.label}
            hint={edit.multiline ? 'Enter to save · Shift+Enter for a new line' : 'Enter to save'}
            error={error}
          >
            {(id, describedBy) =>
              edit.multiline ? (
                <Textarea {...props} ref={textareaRef} id={id} aria-describedby={describedBy} rows={4} />
              ) : (
                <Input {...props} ref={inputRef} id={id} aria-describedby={describedBy} />
              )
            }
          </Field>
        </form>
      )}
    </Modal>
  );
}
